import { Classes } from "@blueprintjs/core";
import { useParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { Box, Flex } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { PermissionErrorPage } from "@/components/pages_public/403";
import { NotFound } from "@/components/pages_public/404/NotFound";
import { ReviewActions } from "@/components/pieces/coaching/reviews/ReviewActions";
import { ReviewHeader } from "@/components/pieces/coaching/reviews/ReviewHeader";
import { ReviewSidebar } from "@/components/pieces/coaching/reviews/review-sidebar";
import { ServiceChat } from "@/components/pieces/coaching/ServiceChat";
import { ProductDetails } from "@/components/pieces/products/ProductDetails";
import { SubmittedInformation } from "@/components/pieces/products/SubmittedInformation";
import { getUser } from "@/components/session/JwtTokenManager";
import {
  CARD_CLASSES,
  SIDE_NAV_WIDTH,
  SITE_NAME,
  STANDARD_APP_MAX_WIDTH,
} from "@/css/constants";
import { useFullReviewQuery } from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";

export function ReviewSkeleton() {
  return (
    <>
      <ReviewHeader loading={true} />
      <Box pb={6}>
        <Box maxWidth={STANDARD_APP_MAX_WIDTH} m="0 auto" px={[3, 24]}>
          <Box mt={4} width={1}>
            <Flex flexWrap="wrap">
              <Box
                width={["100%", "100%", "100%", 450]}
                backgroundColor="#FFF"
                className={`${CARD_CLASSES} ${Classes.SKELETON}`}
                height={500}
                p={[3, 3, 24]}
                mb={[24, 24, 24, 0]}
              />
              <Box
                width={["100%", "auto"]}
                style={{ flex: 1 }}
                ml={[0, 0, 0, 24]}
                backgroundColor="#FFF"
                className={`${CARD_CLASSES} ${Classes.SKELETON}`}
                height={500}
              />
            </Flex>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export function ReviewContainer({ userId }: { userId?: string }) {
  const { reviewId } = useParams();
  const { data } = useFullReviewQuery({
    variables: {
      id: reviewId || "",
      customerId: userId,
    },
    skip: !reviewId,
  });

  if (!reviewId) return <NotFound />;

  if (!data) {
    return (
      <>
        <Nav />
        <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
          <ReviewSkeleton />
        </Box>
      </>
    );
  }

  const { review } = data;
  const { product } = review || {};

  if (!review || !product) return <PermissionErrorPage />;

  const currentUser = getUser();
  const isCoach = currentUser?.id === review?.coach?.id;
  const answers = ensureNonNullArray(review?.formFieldAnswers);

  return (
    <>
      <SEOHead
        title={`${review?.product?.name} - ${SITE_NAME}`}
        description={`See details of your ${SITE_NAME} sesion`}
        canonicalPath={`/sessions/${review?.id}`}
        image="https://placement-pub.s3.us-east-2.amazonaws.com/sessions-meta.png"
      />
      <Nav />
      <Box ml={[0, 0, SIDE_NAV_WIDTH]}>
        <ReviewHeader review={review} loading={false} />
        <Flex
          maxWidth={STANDARD_APP_MAX_WIDTH}
          m="0 auto"
          px={[3, 24]}
          pb={6}
          flexDirection={["column", "column", "column", "row"]}
        >
          <Box mt={4} flex={1}>
            <ReviewActions review={review} isCoach={isCoach} />
            <ReviewSidebar review={review} />

            <SubmittedInformation answers={answers} />

            <Box
              mt={4}
              backgroundColor="#FFF"
              className={CARD_CLASSES}
              p={[3, 3, 24]}
              mb={[24, 24, 24, 0]}
            >
              <Box fontSize={18} fontWeight={500}>
                About This Service
              </Box>
              <ProductDetails
                product={product}
                coachPrice={review.creditPrice}
              />
            </Box>
          </Box>
          <Box mt={4} width={["100%", "100%", "100%", 400]} ml={[0, 0, 0, 3]}>
            <ServiceChat
              isCoach={isCoach}
              customer={review.customer}
              coach={review.coach}
              chatChannelId={review.chatChannelId ?? ""}
            />
          </Box>
        </Flex>
      </Box>
    </>
  );
}

export function ReviewPage() {
  return (
    <>
      <Authenticate maxWidth="100%" layout="dashboard">
        <ReviewContainer />
      </Authenticate>
    </>
  );
}
